import { Table } from "@tanstack/react-table";
import isEqual from "lodash/isEqual";
import { EventKey } from "PFTypes/event_key";
import React, { PropsWithChildren, RefObject, useCallback, useEffect, useRef, useState } from "react";

import { getCoordFromEvent, normalizedRange } from "./cell_selection_utils";
import css from "./interactive_table.module.scss";
import { createInteractiveTableStore } from "./interactive_table.store";
import {
  ArrowDirection,
  ClearEvent,
  CopyEvent,
  DragAxis,
  EditTrigger,
  FillEvent,
  PasteEvent
} from "./interactive_table.types";
import { getCellTextInput, isInsideDialog, isPrintableKey } from "./interactive_table.utils";
import { InteractiveTableStoreContext } from "./interactive_table_store_context";
import { useAutoScroll } from "./use_auto_scroll";
import { useCellCopyPaste } from "./use_cell_copy_paste";

type InteractiveTableProps = PropsWithChildren<{
  dragAxis?: DragAxis;
  onFill?: (event: FillEvent) => void;
  onCopy?: (event: CopyEvent) => void;
  onPaste?: (event: PasteEvent) => void;
  onClear?: (event: ClearEvent) => void;
  /** Ref created by the consumer and passed to the child Table component.
   * Table component writes the TanStack table instance into it.
   * Used to read row data for interactive operations. */
  tableInstanceRef?: RefObject<Table<any> | null>;
  /** Ref to the external scroll container that wraps InteractiveTable.
   * When provided, InteractiveTable will auto-scroll to keep the active
   * cell visible during keyboard navigation and mouse drag. **/
  scrollableContainerRef?: RefObject<HTMLElement | null>;
  nonInteractiveLastColumn?: boolean;
  // Silently blocks if the clipboard content was copied from a different column than the paste anchor column
  restrictPasteToSourceColumn?: boolean;
}>;

const ARROW_KEYS = new Set<string>([
  EventKey.ArrowUp,
  EventKey.ArrowDown,
  EventKey.ArrowLeft,
  EventKey.ArrowRight
]);

export const InteractiveTable = ({
  children,
  dragAxis = DragAxis.Both,
  onFill,
  onCopy,
  onPaste,
  onClear,
  tableInstanceRef,
  scrollableContainerRef,
  nonInteractiveLastColumn = false,
  restrictPasteToSourceColumn = false
}: InteractiveTableProps) => {
  const [store] = useState(createInteractiveTableStore);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isMouseDownInsideRef = useRef(false);

  const { copySelection, pasteFromAnchor } = useCellCopyPaste({
    store,
    tableInstanceRef,
    onCopy,
    onPaste,
    restrictPasteToSourceColumn
  });

  useAutoScroll(store, scrollableContainerRef, wrapperRef, dragAxis);

  const getCoord = useCallback(
    (event: React.MouseEvent | MouseEvent) => {
      const coord = getCoordFromEvent(event);

      if (!coord || !nonInteractiveLastColumn) {
        return coord;
      }

      const maxCol = (tableInstanceRef?.current?.getAllColumns().length ?? 0) - 2;
      return coord.colIndex > maxCol ? null : coord;
    },
    [nonInteractiveLastColumn, tableInstanceRef]
  );

  const getClickableCoord = (event: React.MouseEvent) => {
    const coord = getCoord(event);
    const { editModeCoord } = store.getState();

    if (!editModeCoord) {
      return coord;
    }

    return !isEqual(editModeCoord, coord) ? coord : null;
  };

  const handleBlur = (event: React.FocusEvent) => {
    if (
      wrapperRef.current?.contains(event.relatedTarget as Node) ||
      isInsideDialog(event.relatedTarget as HTMLElement)
    ) {
      return;
    }

    store.getState().actions.clearSelection();
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    const isInsideInteractiveTable = wrapperRef.current?.contains(event.target as Node);

    if (!isInsideInteractiveTable) {
      return;
    }

    // Skip for drag handle
    if (store.getState().isFillDragging) {
      return;
    }

    const coord = getClickableCoord(event);
    if (!coord) {
      return;
    }

    store.getState().actions.setAnchor(coord);
  };

  const handleDoubleClick = (event: React.MouseEvent) => {
    const isInsideInteractiveTable = wrapperRef.current?.contains(event.target as Node);

    if (!isInsideInteractiveTable) {
      return;
    }

    const coord = getClickableCoord(event);
    if (!coord) {
      return;
    }

    const tableEl = wrapperRef.current?.querySelector("table");
    const textInput = tableEl ? getCellTextInput(tableEl, coord) : null;
    const isNumberInput = textInput?.type === "number" || textInput?.inputMode === "decimal";
    const trigger = isNumberInput ? EditTrigger.Overwrite : EditTrigger.CursorAtEnd;

    store.getState().actions.enterEditMode(coord, trigger);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const { selectionRange, editModeCoord, actions } = store.getState();

    if (!selectionRange || isInsideDialog(document.activeElement as HTMLElement)) {
      return;
    }

    const getBounds = () => {
      const table = wrapperRef.current?.querySelector("table");
      const rowCount = table?.querySelectorAll("tbody tr").length ?? 0;
      const rawColCount = tableInstanceRef?.current?.getAllColumns().length ?? 0;
      const colCount = nonInteractiveLastColumn ? rawColCount - 1 : rawColCount;
      return { rowCount, colCount };
    };

    if (editModeCoord) {
      if (event.key === EventKey.Escape) {
        event.preventDefault();
        actions.exitEditMode();
        // Return browser focus to the grid wrapper so subsequent arrow-key /
        // keyboard shortcuts are received by handleKeyDown again.
        wrapperRef.current?.focus({ preventScroll: true });
      }

      return;
    }

    if ((event.metaKey || event.ctrlKey) && event.key === "c") {
      event.preventDefault();
      copySelection();
      return;
    }

    if ((event.metaKey || event.ctrlKey) && event.key === "v") {
      event.preventDefault();
      pasteFromAnchor(getBounds());
      return;
    }

    if (ARROW_KEYS.has(event.key)) {
      event.preventDefault();
      actions.moveSelection(event.key as ArrowDirection, event.shiftKey, dragAxis, getBounds());
      return;
    }

    if (event.key === EventKey.Escape) {
      event.preventDefault();
      actions.clearSelection();
      wrapperRef.current?.focus({ preventScroll: true });
      return;
    }

    if (event.key === EventKey.Enter) {
      event.preventDefault();
      actions.enterEditMode(selectionRange.anchor, EditTrigger.CursorAtEnd);
      return;
    }

    if (event.key === EventKey.Tab) {
      const bounds = getBounds();
      const { anchor } = selectionRange;
      const isFirstCell = anchor.rowIndex === 0 && anchor.colIndex === 0;
      const isLastCell = anchor.rowIndex === bounds.rowCount - 1 && anchor.colIndex === bounds.colCount - 1;

      if (event.shiftKey) {
        if (isFirstCell) {
          actions.clearSelection();
          return;
        }

        event.preventDefault();
        actions.tabReverse(bounds);
      } else {
        if (isLastCell) {
          actions.clearSelection();
          return;
        }

        event.preventDefault();
        actions.tabAdvance(bounds);
      }
      return;
    }

    if (event.key === EventKey.Backspace || event.key === EventKey.Delete) {
      event.preventDefault();
      if (onClear) {
        onClear({ targetRange: normalizedRange(selectionRange) });
      }
      return;
    }

    // Any printable character while a cell is active — enter overwrite edit mode.
    if (isPrintableKey(event)) {
      const tableEl = wrapperRef.current?.querySelector("table");
      const textInput = tableEl ? getCellTextInput(tableEl, selectionRange.anchor) : null;
      if (textInput) {
        actions.enterEditMode(selectionRange.anchor, EditTrigger.Overwrite);
      }
    }
  };

  useEffect(() => {
    store.getState().actions.setNonInteractiveLastColumn(nonInteractiveLastColumn);
  }, [store, nonInteractiveLastColumn]);

  // Document-level mouseup + mousemove listeners:
  // - mouseup: end drag even if the mouse is released outside the browser window.
  // - mousemove: track the pointer at the document level so that scroll containers
  //   (TableResizer has overflow:auto) cannot swallow mousemove events, and so that
  //   `elementFromPoint` always resolves the correct <td> regardless of which child
  //   element originally captured the event target.
  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const isInsideInteractiveTable = wrapperRef.current?.contains(event.target as Node) ?? false;
      isMouseDownInsideRef.current = isInsideInteractiveTable;

      if (!isInsideInteractiveTable && !isInsideDialog(event.target as HTMLElement)) {
        store.getState().actions.clearSelection();
      }
    };

    const handleDocumentMouseMove = (event: MouseEvent) => {
      if (event.buttons !== 1) {
        return;
      }

      if (!isMouseDownInsideRef.current) {
        return;
      }

      const { isDragging, isFillDragging, selectionRange, actions } = store.getState();
      if (!isDragging && !isFillDragging) {
        if (selectionRange) {
          actions.startDrag();
        }
        return;
      }

      const coord = getCoord(event);
      if (!coord) {
        return;
      }

      if (isDragging) {
        actions.extendSelection(coord, dragAxis);
      }
      if (isFillDragging) {
        actions.extendFillRange(coord, dragAxis);
      }
    };

    const handleDocumentMouseUp = () => {
      isMouseDownInsideRef.current = false;
      const { isFillDragging } = store.getState();
      store.getState().actions.endDrag(onFill);

      // Refocus after drag end so the handleBlur is triggered correctly
      if (isFillDragging) {
        wrapperRef.current?.focus({ preventScroll: true });
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    document.addEventListener("mousemove", handleDocumentMouseMove);
    document.addEventListener("mouseup", handleDocumentMouseUp);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
      document.removeEventListener("mousemove", handleDocumentMouseMove);
      document.removeEventListener("mouseup", handleDocumentMouseUp);
    };
  }, [store, onFill, dragAxis, getCoord]);

  return (
    <InteractiveTableStoreContext.Provider value={store}>
      <div
        ref={wrapperRef}
        role="grid"
        className={css.wrapper}
        onBlur={handleBlur}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {children}
      </div>
    </InteractiveTableStoreContext.Provider>
  );
};
